import { useSessionKey } from "@auth/sessionStore";
import { useUser } from "@context/UserContext";
import {
  BoardIcon,
  BrianGlyph,
  BrianWordmark,
  CalendarIcon,
  ChecklistIcon,
  EnvelopeIcon,
  GearIcon,
  type IconProps,
  NavItem,
  NavRail,
  PhoneIcon,
} from "@scribenote/brian-design-system";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useOpenTodoCount } from "@todos/openTodos";
import { canAccessAdmin } from "@users/userClient";
import type { ComponentType } from "react";
import { useState, type MouseEvent } from "react";

type NavPath = "/schedule" | "/board" | "/calls" | "/email" | "/todos";

const NAV: {
  label: string;
  path: NavPath;
  icon: ComponentType<IconProps>;
  actionName: `Nav: ${string}`;
}[] = [
  {
    label: "Schedule",
    path: "/schedule",
    icon: CalendarIcon,
    actionName: "Nav: Schedule",
  },
  {
    label: "Clinic board",
    path: "/board",
    icon: BoardIcon,
    actionName: "Nav: Clinic board",
  },
  { label: "Calls", path: "/calls", icon: PhoneIcon, actionName: "Nav: Calls" },
  {
    label: "Email",
    path: "/email",
    icon: EnvelopeIcon,
    actionName: "Nav: Email",
  },
  {
    label: "To-dos",
    path: "/todos",
    icon: ChecklistIcon,
    actionName: "Nav: To-dos",
  },
];

const COLLAPSED_KEY = "brian.navCollapsed";

const readCollapsed = (): boolean => {
  try {
    const saved = localStorage.getItem(COLLAPSED_KEY);
    return saved === null
      ? window.matchMedia("(max-width: 767px)").matches
      : saved === "1";
  } catch {
    return window.matchMedia("(max-width: 767px)").matches;
  }
};

export const WorkstationNav = () => {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const todoCount = useOpenTodoCount(useSessionKey());
  const { currentUser } = useUser();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = () =>
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, prev ? "0" : "1");
      } catch {
        // Unavailable storage just means the choice isn't remembered.
      }
      return !prev;
    });

  const go = (e: MouseEvent, to: NavPath | "/settings") => {
    e.preventDefault();
    void navigate({ to });
  };

  return (
    <NavRail
      wordmark={<BrianWordmark tone="desk" mark={false} className="text-2xl" />}
      mark={<BrianGlyph tone="desk" className="size-[26px]" />}
      collapsed={collapsed}
      onToggleCollapsed={toggle}
      footer={
        canAccessAdmin(currentUser) && (
          <NavItem
            href="/settings"
            icon={<GearIcon />}
            label="Clinic settings"
            active={pathname.startsWith("/settings")}
            onClick={(e) => go(e, "/settings")}
            data-dd-action-name="Nav: Clinic settings"
          />
        )
      }
    >
      {NAV.map((item) => (
        <NavItem
          key={item.path}
          href={item.path}
          icon={<item.icon />}
          label={item.label}
          count={item.path === "/todos" ? todoCount : undefined}
          active={pathname.startsWith(item.path)}
          onClick={(e) => go(e, item.path)}
          data-dd-action-name={item.actionName}
        />
      ))}
    </NavRail>
  );
};
